import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { useUnread } from "@/api/engage";
import { useMe } from "@/api/hooks";
import { useApprovals } from "@/api/ops";
import { useTicketCounts } from "@/api/service";
import { useStatus } from "@/api/support";
import { GlobalSearch } from "@/components/crm/GlobalSearch";
import { Icon, type IconName } from "@/components/icons";
import { Avatar, Spinner, formatDate } from "@/components/ui";
import { useAuth } from "@/features/auth/AuthProvider";
import { DealerPortal } from "@/features/dealer/DealerPortal";
import { PoweredBy } from "@/components/PoweredBy";
import { BrandMark, useBrand } from "@/lib/branding";
import { can } from "@/lib/permissions";
import { displayPrefs, setDisplayPrefs, zoneLabel } from "@/lib/time";

const navClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ${
    isActive ? "bg-brand-soft text-brand" : "text-muted hover:bg-canvas hover:text-ink"
  }`;

interface NavItem { to: string; label: string; icon: IconName; show: boolean; settings?: boolean }

/** A strip under the header while there is a problem or maintenance is near. */
function ServiceBanner() {
  const status = useStatus(5 * 60_000).data;
  if (!status) return null;
  const soon = Date.now() + 48 * 3600_000;
  const notice = status.incidents.find((i) => new Date(i.starts_at).getTime() <= soon);
  if (status.status === "operational" && !notice) return null;
  const text = notice
    ? new Date(notice.starts_at).getTime() > Date.now()
      ? `Planned maintenance ${formatDate(notice.starts_at)}: ${notice.title}`
      : notice.title
    : status.summary;
  return (
    <Link to="/status" role="status" className={`block px-4 py-2 text-center text-sm font-medium ${status.status === "outage" ? "bg-danger text-white" : "bg-warn/15 text-warn"}`}>
      {text} · details
    </Link>
  );
}

export function AppLayout() {
  const { data: me } = useMe();
  const unread = useUnread(!!me && can(me.permissions, "inbox:read")).data?.unread ?? 0;
  const approver = !!me && (can(me.permissions, "quotes:approve") || can(me.permissions, "orders:approve"));
  const approvals = useApprovals(approver).data;
  const waiting = approvals ? approvals.quotes.length + approvals.orders.length : 0;
  const overdueTickets = useTicketCounts(!!me && can(me.permissions, "service:read")).data?.overdue ?? 0;
  const { logout } = useAuth();
  const navigate = useNavigate();
  const brand = useBrand();

  if (!me) return <div className="p-10"><Spinner /></div>;
  // Dealer and distributor staff get their own portal and nothing else.
  if (me.user.role.key === "dealer") return <DealerPortal />;
  // Every date on every page is formatted for this person's zone and language.
  setDisplayPrefs({ timeZone: me.user.timezone, locale: me.user.locale });
  const { timeZone, locale } = displayPrefs();
  const perms = me.permissions;
  const links: NavItem[] = ([
    { to: "/", label: "Home", icon: "home", show: true },
    { to: "/inbox", label: "Inbox", icon: "inbox", show: can(perms, "inbox:read") },
    { to: "/approvals", label: "Approvals", icon: "check", show: approver },
    { to: "/deals", label: "Deals", icon: "board", show: can(perms, "deals:read") },
    { to: "/quotes", label: "Quotes", icon: "quote", show: can(perms, "quotes:read") },
    { to: "/orders", label: "Orders", icon: "order", show: can(perms, "orders:read") },
    { to: "/accounts", label: "Accounts", icon: "accounts", show: can(perms, "accounts:read") },
    { to: "/contacts", label: "Contacts", icon: "contacts", show: can(perms, "contacts:read") },
    { to: "/products", label: "Products", icon: "product", show: can(perms, "products:read") },
    { to: "/tasks", label: "Tasks", icon: "tasks", show: can(perms, "activities:read") },
    { to: "/service", label: "Service", icon: "tool", show: can(perms, "service:read") },
    { to: "/installed-base", label: "Installed base", icon: "product", show: can(perms, "service:read") },
    { to: "/amc", label: "AMC contracts", icon: "ledger", show: can(perms, "service:read") },
    { to: "/samples", label: "Samples", icon: "product", show: can(perms, "deals:read") },
    { to: "/visits", label: "Field visits", icon: "visit", show: can(perms, "visits:create") },
    { to: "/trade-shows", label: "Trade shows", icon: "store", show: can(perms, "contacts:create") },
    { to: "/reports", label: "Reports", icon: "chart", show: can(perms, "deals:read") },
    { to: "/leads", label: "Lead capture", icon: "form", show: can(perms, "leads:manage") },
    { to: "/sequences", label: "Sequences", icon: "sequence", show: can(perms, "sequences:enrol") },
    { to: "/automations", label: "Automations", icon: "zap", show: can(perms, "automations:manage") },
    { to: "/help", label: "Help and support", icon: "help", show: true },
    { to: "/settings/channels", label: "Channels", icon: "whatsapp", show: can(perms, "settings:channels"), settings: true },
    { to: "/settings/company", label: "Company profile", icon: "company", show: can(perms, "settings:company"), settings: true },
    { to: "/settings/pricing", label: "Pricing", icon: "tag", show: can(perms, "cpq:manage"), settings: true },
    { to: "/settings/territories", label: "Territories", icon: "map", show: can(perms, "territories:manage"), settings: true },
    { to: "/settings/accounting", label: "Accounting", icon: "ledger", show: can(perms, "settings:accounting"), settings: true },
    { to: "/settings/integrations", label: "Payments and e-sign", icon: "tag", show: can(perms, "settings:integrations"), settings: true },
    { to: "/data", label: "Import and export", icon: "import", show: can(perms, "data:import") || can(perms, "data:export"), settings: true },
    { to: "/settings/pipelines", label: "Pipelines", icon: "deals", show: can(perms, "settings:pipelines"), settings: true },
    { to: "/settings/fields", label: "Custom fields", icon: "settings", show: can(perms, "settings:fields"), settings: true },
    { to: "/settings/users", label: "Users and roles", icon: "contacts", show: can(perms, "users:read"), settings: true },
    { to: "/settings/sso", label: "Single sign-on", icon: "shield", show: can(perms, "settings:sso"), settings: true },
    { to: "/settings/plan", label: "Plan and usage", icon: "chart", show: can(perms, "billing:manage"), settings: true },
    { to: "/settings/developer", label: "Developers", icon: "code", show: can(perms, "settings:developer"), settings: true },
  ] satisfies NavItem[]).filter((l) => l.show);
  const main = links.filter((l) => !l.settings);
  const settings = links.filter((l) => l.settings);

  return (
    <div className="flex min-h-full">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface md:flex">
        <div className="flex items-center gap-2 px-5 py-5">
          <BrandMark />
          <span className="text-[15px] font-semibold tracking-tight">{brand.name}</span>
        </div>
        <nav className="flex-1 space-y-1 px-3" aria-label="Main">
          {main.map((l) => <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navClass}><Icon name={l.icon} />{l.label}
            {l.to === "/inbox" && unread > 0 && <span className="ml-auto rounded-full bg-brand px-1.5 text-xs font-semibold text-white" aria-label={`${unread} unread`}>{unread}</span>}
            {l.to === "/service" && overdueTickets > 0 && <span className="ml-auto rounded-full bg-danger px-1.5 text-xs font-semibold text-white" aria-label={`${overdueTickets} overdue`}>{overdueTickets}</span>}
            {l.to === "/approvals" && waiting > 0 && <span className="ml-auto rounded-full bg-warn px-1.5 text-xs font-semibold text-white" aria-label={`${waiting} waiting`}>{waiting}</span>}</NavLink>)}
          {settings.length > 0 && <p className="px-3 pb-1 pt-5 text-xs font-semibold uppercase tracking-wide text-muted">Settings</p>}
          {settings.map((l) => <NavLink key={l.to} to={l.to} className={navClass}><Icon name={l.icon} />{l.label}</NavLink>)}
        </nav>
        <div className="border-t border-line p-4 text-xs text-muted">
          Workspace <span className="font-medium text-ink">{me.tenant.slug}</span>
          <p className="mt-1 flex gap-3"><Link to="/legal/terms" className="hover:text-brand">Terms</Link><Link to="/legal/privacy" className="hover:text-brand">Privacy</Link><Link to="/status" className="hover:text-brand">Status</Link></p>
          <PoweredBy className="mt-2" />
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-line bg-surface px-4 py-3 sm:px-6">
          <span className="md:hidden" title={brand.name}><BrandMark className="h-7 w-7" /></span>
          <div className="hidden min-w-0 truncate text-sm lg:block">
            <span className="font-semibold">{me.tenant.name}</span>
            <span className="ml-2 rounded-full bg-brand-soft px-2 py-0.5 text-xs font-medium capitalize text-brand">{me.tenant.industry_pack}</span>
          </div>
          <div className="flex flex-1 justify-center"><GlobalSearch /></div>
          <div className="flex items-center gap-3">
            <Link to="/settings/profile" className="hidden text-right text-sm hover:text-brand sm:block" title={`Times shown in ${timeZone}`}>
              <div className="font-medium">{me.user.full_name}</div>
              <div className="text-xs text-muted">{me.user.role.name} · <span data-testid="zone-label">{zoneLabel()}</span></div>
            </Link>
            <Link to="/settings/profile" aria-label="My profile" className="rounded-full focus-visible:outline-2 focus-visible:outline-brand"><Avatar name={me.user.full_name} /></Link>
            <button type="button" className="btn-ghost whitespace-nowrap" onClick={() => void logout().then(() => navigate("/login"))}>Sign out</button>
          </div>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-line bg-surface px-3 py-2 md:hidden" aria-label="Mobile">
          {links.map((l) => <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navClass}>{l.label}</NavLink>)}
        </nav>
        <ServiceBanner />
        <main key={`${timeZone}|${locale ?? ""}`} className="mx-auto w-full max-w-7xl flex-1 p-4 sm:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
